import React from 'react';
import { ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline';

interface TagInfo {
  text: string;
  type: 'warning' | 'danger';
  urgent: boolean;
}

interface AcademicExpirationInlineTagProps {
  tagInfo: TagInfo | null;
  onClick: () => void;
}

/**
 * Inline tag component to display academic expiration warning next to plan info
 */
const AcademicExpirationInlineTag: React.FC<AcademicExpirationInlineTagProps> = ({ tagInfo, onClick }) => {
  if (!tagInfo) return null;

  const { text, type, urgent } = tagInfo;

  const getTagClasses = () => {
    const baseClasses =
      'inline-flex items-center px-2 py-1 mb-2 rounded-md text-xs font-medium cursor-pointer transition-colors duration-200 ml-2';

    if (type === 'danger') {
      return urgent
        ? `${baseClasses} bg-red-100 text-red-800 border border-red-200 hover:bg-red-200`
        : `${baseClasses} bg-red-50 text-red-700 border border-red-100 hover:bg-red-100`;
    } else {
      return `${baseClasses} bg-yellow-50 text-yellow-700 border border-yellow-200 hover:bg-yellow-100`;
    }
  };

  const getIcon = () => {
    if (type === 'danger' && urgent) {
      return <ExclamationTriangleIcon className="w-3 h-3 mr-1" />;
    }
    return <ClockIcon className="w-3 h-3 mr-1" />;
  };

  const handleClick = e => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) {
      onClick();
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  };

  return (
    <span
      className={getTagClasses()}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      title="Click to manage your academic account"
    >
      {getIcon()}
      <span>{text}</span>
    </span>
  );
};

export default AcademicExpirationInlineTag;
